import { ComponentType, useState } from 'react';
import { Control, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { collectFormErrors } from '../../../../utils/collect-form-errors';
import { Button } from '../../../atoms/button/button';
import { FormError } from '../../../atoms/form-error/form-error';
import { Header } from '../../../atoms/header/header';
import { Text } from '../../../atoms/text/text';
import { createSubmitHandler } from '../handle-submit';

import { FormFields } from './register-form-fields';
import { Inputs, RegistrationMethod } from './types';

type RenderCheckboxesArgs = {
  readonly control: Control<Inputs>;
  readonly errors: Partial<Record<keyof Inputs, { message?: string }>>;
};

type RenderCheckboxesComponent = ComponentType<RenderCheckboxesArgs>;
type RenderInformationComponent = ComponentType;

const FormActions = ({
  control,
  errors,
  formErrors,
  isSubmitting,
  preSignUpText,
  renderCheckboxes: RenderCheckboxes,
  submitButtonKey,
}: {
  readonly control: Control<Inputs>;
  readonly errors: Partial<Record<keyof Inputs, { message?: string }>>;
  readonly formErrors: string[];
  readonly isSubmitting: boolean;
  readonly preSignUpText?: { text: string; title: string };
  readonly renderCheckboxes?: RenderCheckboxesComponent;
  readonly submitButtonKey: string;
}) => {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-7.5 mt-7.5">
      {RenderCheckboxes && <RenderCheckboxes control={control} errors={errors} />}
      <FormError error={formErrors.length > 0 ? formErrors : null} icon="alert-circle" />
      <Button className="w-full" loading={isSubmitting}>
        {t(submitButtonKey)}
      </Button>
      {preSignUpText && (
        <div className="flex flex-col gap-2 mb-6">
          {preSignUpText.title && <Header level="h4">{preSignUpText.title}</Header>}
          {preSignUpText.text && <Text size="sm">{preSignUpText.text}</Text>}
        </div>
      )}
    </div>
  );
};

type OnSubmit = ({ username, password }: { username: string; password: string }) => void;

type RegisterFormProps = {
  readonly onSubmit: OnSubmit;
  readonly preSignUpText?: { title: string; text: string };
  readonly passwordLess?: boolean;
  readonly signInUrl?: string;
  readonly defaultMethod?: RegistrationMethod;
  readonly headerKey?: string;
  readonly subHeaderKey?: string;
  readonly showRegistrationMethodToggle?: boolean;
  readonly switchToEmailLabelKey?: string;
  readonly switchToMobileLabelKey?: string;
  readonly renderInformation?: RenderInformationComponent;
  readonly renderCheckboxes?: RenderCheckboxesComponent;
  readonly submitButtonKey: string;
  readonly allowedEmailDomains?: string[];
};
const RegisterForm = ({
  onSubmit,
  preSignUpText,
  passwordLess = false,
  signInUrl,
  defaultMethod,
  headerKey,
  subHeaderKey,
  showRegistrationMethodToggle = true,
  switchToEmailLabelKey,
  switchToMobileLabelKey,
  renderInformation,
  renderCheckboxes,
  submitButtonKey,
  allowedEmailDomains,
}: RegisterFormProps) => {
  const {
    register,
    handleSubmit,
    control,
    clearErrors,
    setError,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<Inputs>();
  const [showPassword, setShowPassword] = useState<boolean>();
  const { t } = useTranslation();
  const formErrors = collectFormErrors(errors, t);

  return (
    <form onSubmit={handleSubmit(createSubmitHandler({ onSubmit, setError }))}>
      <FormFields
        allowedEmailDomains={allowedEmailDomains}
        clearErrors={clearErrors}
        control={control}
        defaultMethod={defaultMethod}
        errors={errors}
        headerKey={headerKey ?? 'auth.register.mainHeader'}
        passwordInputType={showPassword ? 'text' : 'password'}
        passwordLess={passwordLess}
        register={register}
        renderInformation={renderInformation}
        setError={setError}
        setShowPassword={setShowPassword}
        setValue={setValue}
        showRegistrationMethodToggle={showRegistrationMethodToggle}
        signInUrl={signInUrl}
        subHeaderKey={subHeaderKey}
        switchToEmailLabelKey={switchToEmailLabelKey ?? 'auth.register.switchToEmailUsernameLabel'}
        switchToMobileLabelKey={switchToMobileLabelKey ?? 'auth.register.switchToMobileUsernameLabel'}
        watchPassword={watch('password')}
      />
      <FormActions
        control={control}
        errors={errors}
        formErrors={formErrors}
        isSubmitting={isSubmitting}
        preSignUpText={preSignUpText}
        renderCheckboxes={renderCheckboxes}
        submitButtonKey={submitButtonKey}
      />
    </form>
  );
};

export { RegisterForm };
